import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qgammonAuthHeaders, qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import {
  isMatchmakingCancelReason,
  ServerEvents,
  type MatchmakingCancelledPayload,
  type MatchmakingCancelReason,
  type MatchmakingPairedPayload,
  type MatchmakingQueuedPayload,
} from '@qlashgeminiz/qgammon-types';

export type RankedQueueStatus = 'idle' | 'queueing' | 'queued' | 'paired';

/**
 * Why the search is no longer running. Surfaced by `RankedQueueSheet` so the
 * player is never just dropped back to the lobby with no explanation.
 */
export type RankedQueueNotice =
  /** `POST /matchmaking/queue` → 409: already at the concurrent-match cap. */
  | { kind: 'atMatchCap'; max: number | null }
  /**
   * The SERVER took us out of the queue (`mm:cancelled`). `reason` is only ever
   * one the shared types package knows about; anything else — including reasons
   * added to the protocol after this bundle shipped — degrades to `'unknown'`
   * and gets generic copy. Never switch exhaustively on this: the server's list
   * grows, and the wire schema is deliberately liberal (`z.string()`) for
   * exactly that reason.
   */
  | { kind: 'cancelled'; reason: MatchmakingCancelReason | 'unknown' }
  /** The request itself failed (network / 5xx). */
  | { kind: 'failed' };

export interface RankedQueue {
  status: RankedQueueStatus;
  /** Server-confirmed enqueue time (from `mm:queued`), used for the elapsed clock. */
  enqueuedAt: number | null;
  /** Set once the server pairs us — the caller navigates to the match. */
  paired: MatchmakingPairedPayload | null;
  /** Set when the search stopped for a reason worth telling the player. */
  notice: RankedQueueNotice | null;
  /** POST the queue, then wait for `mm:queued`. */
  join: () => void;
  /** DELETE the queue and reset to idle. */
  cancel: () => void;
  /** Acknowledge the notice (closes the sheet). */
  dismissNotice: () => void;
}

/**
 * Drives the qgammon-gameserver ranked matchmaking queue from the TMA.
 *
 * Lifecycle: opens ONE Socket.IO connection to the gameserver with the same
 * handshake auth `useBackgammonSocket` uses (`qgammonSocketAuth()`: per-user
 * Logto `auth.accessToken`, else dev-bypass) but DOES NOT `match:join` — a socket
 * that only completes the handshake still receives the `mm:*` events, which the
 * server fans out to every one of the user's connected sockets (UserSocketIndex).
 * The per-user Logto identity is the fix for two humans sharing one userId and
 * colliding on 409 already_queued.
 *
 * `join()` POSTs `/matchmaking/queue` then waits for `mm:queued`; a 409
 * `already_queued` is treated as success (we're in the queue, just on another
 * tab/socket), while a 409 `too_many_active_matches` means the server refused
 * the search outright because we're at the concurrent-match cap — that becomes
 * a `notice`, not a silent no-op. `cancel()` DELETEs the queue. On `mm:paired`
 * we stash the payload and let the CALLER navigate to the match route. On
 * `mm:cancelled` we reset to idle and, unless WE asked for it (`reason: 'user'`),
 * raise a `notice` — notably `capped`, which the pairing worker sends when the
 * match cap filled up from another route while we were waiting.
 *
 * Pairing can take a while with few players (±50 ELO, widening to ±200 after
 * 30s), so the socket stays open for the whole wait. On unmount we cancel the
 * queue (DELETE + disconnect) for predictable UX — leaving the page does NOT keep
 * you queued.
 */
export function useRankedQueue(): RankedQueue {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<RankedQueueStatus>('idle');
  const [enqueuedAt, setEnqueuedAt] = useState<number | null>(null);
  const [paired, setPaired] = useState<MatchmakingPairedPayload | null>(null);
  const [notice, setNotice] = useState<RankedQueueNotice | null>(null);

  // Latest status, read inside socket handlers without re-binding the socket.
  const statusRef = useRef(status);
  statusRef.current = status;

  // Open the listening socket once on mount. The `mm:*` events arrive on this
  // socket regardless of whether we ever `match:join`.
  useEffect(() => {
    // Handshake auth is resolved asynchronously (it may mint/refresh a Logto
    // token). `cancelled` guards an unmount that races the token resolution.
    let cancelled = false;

    void qgammonSocketAuth().then((auth) => {
      if (cancelled) return;
      const socket = io(env.qgammonWsUrl, { ...QGAMMON_SOCKET_OPTIONS, auth });
      socketRef.current = socket;

      socket.on(ServerEvents.MatchmakingQueued, (p: MatchmakingQueuedPayload) => {
        setEnqueuedAt(Date.parse(p.enqueuedAt) || Date.now());
        // Only advance to `queued` if we're still mid-join — never clobber `paired`.
        setStatus((cur) => (cur === 'queueing' || cur === 'idle' ? 'queued' : cur));
      });
      socket.on(ServerEvents.MatchmakingPaired, (p: MatchmakingPairedPayload) => {
        setPaired(p);
        setStatus('paired');
      });
      socket.on(
        ServerEvents.MatchmakingCancelled,
        (p?: Partial<MatchmakingCancelledPayload>) => {
          // Server dropped us. Only reset if we haven't already been paired — a
          // late cancel must not undo a pairing (statusRef mirrors `status`, so
          // reading it here keeps the state updater itself pure).
          const was = statusRef.current;
          if (was === 'paired') return;
          setStatus('idle');
          setEnqueuedAt(null);
          // Explain ourselves ONLY if this client was actually searching. The
          // gameserver fans `mm:*` out to every socket the user has open, so a
          // queue they started (and lost) on another device would otherwise pop
          // an unexplained sheet on this one.
          if (was !== 'queueing' && was !== 'queued') return;
          // `user` = we asked for it (Cancel, or the unmount DELETE) — no notice.
          // Anything else deserves an explanation, and a reason this bundle has
          // never heard of degrades instead of leaking a raw protocol token.
          const raw: unknown = p?.reason;
          if (raw === 'user') return;
          setNotice({
            kind: 'cancelled',
            reason: isMatchmakingCancelReason(raw) ? raw : 'unknown',
          });
        },
      );
    });

    return () => {
      cancelled = true;
      // Cancel on unmount: best-effort DELETE so we don't linger in the queue
      // after leaving the page, then drop the socket.
      if (statusRef.current === 'queued' || statusRef.current === 'queueing') {
        void deleteQueue();
      }
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
    // Mount-once: the socket lifecycle is independent of join/cancel calls.
    // Cleanup reads only stable refs and the module-level deleteQueue helper,
    // so there are no reactive dependencies to list here.
  }, []);

  const join = useCallback(() => {
    setNotice(null);
    setPaired(null);
    setStatus('queueing');
    void (async () => {
      try {
        // Logto Bearer if signed in, else dev-bypass headers (dev only).
        const headers = await qgammonAuthHeaders(true);
        const res = await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        });
        if (res.ok) return; // wait for mm:queued to flip to `queued`
        if (res.status === 409) {
          // TWO different 409s share this status, so read the body before
          // deciding. `too_many_active_matches` = the server refused the search
          // because we're at the concurrent-match cap; pretending we're queued
          // would leave the player watching a spinner that can never resolve.
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
            max?: number;
          };
          if (body.error === 'too_many_active_matches') {
            setStatus('idle');
            setNotice({ kind: 'atMatchCap', max: body.max ?? null });
            return;
          }
          // Otherwise: already in the queue from another session of the SAME
          // user (now that identity is per-user, this is a genuine duplicate —
          // no longer two different humans colliding on the shared "Dev" id).
          // We may not get a fresh mm:queued, so optimistically advance.
          // Recover the real enqueuedAt from GET /matchmaking/queue so the
          // elapsed clock is right.
          setStatus((cur) => (cur === 'queueing' ? 'queued' : cur));
          void fetchEnqueuedAt().then((at) => {
            if (at != null) setEnqueuedAt((cur) => cur ?? at);
          });
          setEnqueuedAt((cur) => cur ?? Date.now());
          return;
        }
        throw new Error(`queue failed: ${res.status}`);
      } catch {
        setNotice({ kind: 'failed' });
        setStatus('idle');
      }
    })();
  }, []);

  const cancel = useCallback(() => {
    setStatus('idle');
    setEnqueuedAt(null);
    setPaired(null);
    setNotice(null);
    void deleteQueue();
  }, []);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return { status, enqueuedAt, paired, notice, join, cancel, dismissNotice };
}

/** Fire-and-forget DELETE of the queue entry (used by cancel + unmount cleanup). */
async function deleteQueue(): Promise<void> {
  try {
    const headers = await qgammonAuthHeaders();
    await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, {
      method: 'DELETE',
      headers,
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Recover the server-side enqueue time on a 409 (already queued from another
 * session of the same user). `GET /matchmaking/queue` returns `{ inQueue,
 * waitingMs }`; we derive `enqueuedAt = now - waitingMs` so the elapsed clock is
 * accurate instead of restarting from "now". Returns null if not in queue or on
 * any error (the caller then falls back to Date.now()).
 */
async function fetchEnqueuedAt(): Promise<number | null> {
  try {
    const headers = await qgammonAuthHeaders();
    const res = await fetch(`${env.qgammonWsUrl}/matchmaking/queue`, { headers });
    if (!res.ok) return null;
    const body = (await res.json()) as { inQueue?: boolean; waitingMs?: number };
    if (body.inQueue && typeof body.waitingMs === 'number') {
      return Date.now() - body.waitingMs;
    }
    return null;
  } catch {
    return null;
  }
}
